import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Copy, Plus } from 'lucide-react';
import { Table } from '../components/ui/Table';
import { PushCampaignStatusBadge } from '../components/push-campaigns/PushCampaignStatusBadge';
import { usePolling } from '../hooks/usePolling';
import { useAuth } from '../context/AuthContext';
import { canEditPushCampaigns } from '../utils/pushCampaignPermissions';
import {
  getPushCampaignFailureMessage,
  PUSH_TIMEZONE,
  toPushCampaignDuplicatePayload,
} from '../utils/pushCampaignValidation';
import {
  createPushCampaign,
  getPushCampaign,
  listPushCampaigns,
  type PushAudienceType,
  type PushCampaignListItem,
  type PushCampaignStatus,
} from '../services/resortAdminApi';

const LIST_POLL_INTERVAL_MS = 10000;

const ACTIVE_STATUSES: PushCampaignStatus[] = ['Scheduled', 'Sending', 'Stopping', 'Paused'];

const AUDIENCE_LABELS: Record<PushAudienceType, string> = {
  test_list: 'Test list',
  app_users: 'App users',
  partners: 'Partners',
  couriers: 'Couriers',
};

function formatDateTime(value: string | null): string {
  if (value == null || value === '') {
    return '-';
  }
  const parsed = new Date(value.replace(' ', 'T'));
  if (isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const PushCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const canCreate = canEditPushCampaigns(auth.user?.role ?? null);

  const [campaigns, setCampaigns] = useState<PushCampaignListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [duplicatingName, setDuplicatingName] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await listPushCampaigns(page, pageSize);
      setCampaigns(response.data || []);
      setTotal(response.pagination?.total ?? (response.data?.length || 0));
    } catch (failure) {
      setError((failure as Error)?.message || 'Failed to load push campaigns');
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const hasActive = campaigns.some(x => ACTIVE_STATUSES.includes(x.status));

  const pollFetcher = useCallback(
    (signal: AbortSignal) => listPushCampaigns(page, pageSize, signal),
    [page, pageSize]
  );

  const polling = usePolling(pollFetcher, hasActive, LIST_POLL_INTERVAL_MS);
  const polledData = polling.data;

  useEffect(() => {
    if (polledData == null) {
      return;
    }
    setCampaigns(polledData.data || []);
    setTotal(polledData.pagination?.total ?? (polledData.data?.length || 0));
  }, [polledData]);

  const duplicate = async (name: string) => {
    if (duplicatingName != null) {
      return;
    }
    setDuplicatingName(name);
    setError('');
    try {
      const source = await getPushCampaign(name);
      const created = await createPushCampaign(toPushCampaignDuplicatePayload(source));
      navigate(`/push-campaigns/${encodeURIComponent(created.name)}/edit?step=1`);
    } catch (failure) {
      setError(getPushCampaignFailureMessage(failure, (failure as Error)?.message || 'The campaign could not be duplicated.'));
    } finally {
      setDuplicatingName(null);
    }
  };

  const columns = [
    {
      key: 'status',
      header: 'Status',
      render: (v: PushCampaignStatus) => <PushCampaignStatusBadge status={v} />,
    },
    {
      key: 'campaign_name',
      header: 'Campaign',
      render: (v: string) => <span className="font-medium text-gray-900">{v || '-'}</span>,
    },
    {
      key: 'audience_type',
      header: 'Audience',
      render: (v: PushAudienceType) => <span className="text-gray-900">{AUDIENCE_LABELS[v] || v}</span>,
    },
    {
      key: 'sent_count',
      header: 'Sent',
      render: (_: unknown, row: PushCampaignListItem) => (
        <span className="whitespace-nowrap text-gray-900">{row.sent_count} of {row.recipients_total}</span>
      ),
    },
    {
      key: 'date',
      header: `Date, ${PUSH_TIMEZONE}`,
      render: (_: unknown, row: PushCampaignListItem) => (
        <span className="whitespace-nowrap text-gray-900">
          {formatDateTime(row.finished_at ?? row.started_at ?? row.scheduled_at ?? row.creation)}
        </span>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (_: unknown, row: PushCampaignListItem) => (
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/push-campaigns/${encodeURIComponent(row.name)}`)}
            className="inline-flex items-center text-sm font-medium text-alps-primary hover:text-alps-primary-dark"
          >
            View <ArrowRight className="ml-1 h-4 w-4" />
          </button>
          {canCreate && (
            <button
              onClick={() => duplicate(row.name)}
              disabled={duplicatingName != null}
              className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50"
            >
              <Copy className="mr-1 h-4 w-4" />
              {duplicatingName === row.name ? 'Copying...' : 'Duplicate'}
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Push Campaigns</h1>
          <p className="mt-1 text-sm text-gray-600">Marketing push notifications sent to application users</p>
        </div>
        {canCreate && (
          <button
            onClick={() => navigate('/push-campaigns/new')}
            className="inline-flex items-center rounded-md border border-transparent bg-alps-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-alps-primary-dark"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create
          </button>
        )}
      </div>

      {error !== '' && (
        <div className="border-l-4 border-red-500 bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {polling.isPaused && (
        <div className="flex items-center justify-between border-l-4 border-orange-500 bg-orange-50 p-4">
          <p className="text-sm text-orange-700">
            Live updates stopped after repeated errors. {polling.error ?? ''}
          </p>
          <button
            onClick={polling.retry}
            className="rounded-md border border-orange-300 bg-white px-3 py-1 text-sm font-medium text-orange-700 hover:bg-orange-100"
          >
            Retry
          </button>
        </div>
      )}

      <Table
        columns={columns}
        data={campaigns}
        isLoading={isLoading}
        emptyMessage="No push campaigns yet. Click Create to add one."
        pagination={{ page, page_size: pageSize, total }}
        onPageChange={setPage}
        onPageSizeChange={x => { setPageSize(x); setPage(1); }}
      />
    </div>
  );
};
